/**
 * Single source of truth for ALL marketing/SEO prices shown on the site.
 *
 * Everything reads from src/data/pricing-snapshot.json, which is generated
 * by scripts/sync-pricing.js from the live backend (/api/v2/vehicle-data/*).
 *
 * When backend prices change:
 *   node scripts/sync-pricing.js
 *   node scripts/regenerate-programmatic-pages.js
 *   npm run build && pm2 reload ridenrepair_frontend
 *
 * Templates MUST import from here. No hardcoded ₹ literals in JSX or JSON.
 */

import snapshot from '../data/pricing-snapshot.json';

export const PRICING = snapshot;

/** Format a numeric INR value as "₹1,249" (Indian comma grouping). */
export function formatInr(amount) {
  if (amount == null || isNaN(amount)) return '';
  return '₹' + Number(amount).toLocaleString('en-IN');
}

/**
 * Starting "from" price for a vehicle type or the whole site.
 * type: 'bike' | 'car' | 'ev' | 'ac' | 'site'
 */
export function getStartingPrice(type = 'site') {
  const m = snapshot.marketing || {};
  switch (type) {
    case 'bike': return { amount: m.bike_starting_price, label: m.bike_starting_price_label };
    case 'car':  return { amount: m.car_starting_price,  label: m.car_starting_price_label  };
    case 'ev':   return { amount: m.ev_starting_price,   label: m.ev_starting_price_label   };
    case 'ac':   return { amount: m.ac_starting_price,   label: m.ac_starting_price_label   };
    case 'site':
    default:     return { amount: m.site_starting_price, label: m.site_starting_price_label };
  }
}

/** Convenience: just the label, e.g. "₹450". */
export function startingPriceLabel(type = 'site') {
  return getStartingPrice(type).label;
}

/**
 * Resolve a programmatic-service slug (e.g. 'bike-service', 'bike-oil-change',
 * 'car-ac-repair') to its canonical starting price + label.
 * Falls back to the vehicle-type starting price if the slug is unknown.
 */
export function getServiceStartingPrice(serviceSlug, fallbackVehicleType = 'site') {
  const map = snapshot.service_slug_map || {};
  const entry = map[serviceSlug];
  if (entry) {
    return {
      amount: entry.starting_price,
      label: formatInr(entry.starting_price),
      serviceLabel: entry.starting_label,
      vehicleType: entry.vehicle_type,
      highForRange: entry.high_price_for_range,
      rangeLabel: formatInr(entry.starting_price) + ' - ' + formatInr(entry.high_price_for_range),
    };
  }
  const fallback = getStartingPrice(fallbackVehicleType);
  return {
    amount: fallback.amount,
    label: fallback.label,
    serviceLabel: '',
    vehicleType: fallbackVehicleType,
    highForRange: null,
    rangeLabel: fallback.label,
  };
}

/** Bike service catalog (the 5 SKUs from backend 0-199cc). */
export function getBikeServices() {
  return snapshot.bike && snapshot.bike.services ? snapshot.bike.services : {};
}

/** Car AC tier rows for display in pricing tables. */
export function getAcTiers() {
  return (snapshot.car_ac && snapshot.car_ac.tiers) || [];
}

/**
 * Build the "Bike General Service" hero card (verbatim from backend reality)
 * so templates don't have to hand-construct it. Replaces the old hardcoded
 * { label:'Bike General Service', price:'₹799', features:['Engine oil change',...] }
 * which was a bug — General Service at ₹799 does NOT include engine oil change.
 */
export function getBikeServiceCards() {
  const s = getBikeServices();
  const cards = [];
  if (s.general_service) {
    cards.push({
      label: 'Bike General Service',
      examples: 'Air filter, brakes, chain, spark plug',
      price: formatInr(s.general_service.price),
      features: ['Air filter cleaning', 'Brakes service', 'Chain & clutch', 'Spark plug cleaning'],
      linkTo: '/service',
      btnLabel: 'Book Now',
    });
  }
  if (s.general_service_with_oil) {
    cards.push({
      label: 'General Service + Engine Oil',
      examples: 'Full service incl. engine oil change',
      price: formatInr(s.general_service_with_oil.price),
      features: ['Engine oil change', 'Air filter cleaning', 'Brakes service', 'Chain & spark plug'],
      linkTo: '/service',
      btnLabel: 'Book Now',
      highlight: true,
    });
  }
  if (s.running_repair) {
    cards.push({
      label: 'Bike Repair',
      examples: 'Brake, clutch, electrical, suspension',
      price: formatInr(s.running_repair.price),
      features: ['Diagnosis', 'On-the-spot repair', 'Parts at additional charge', '30-day warranty'],
      linkTo: '/service',
      btnLabel: 'Book Now',
    });
  }
  if (s.puncture) {
    cards.push({
      label: 'Bike Puncture',
      examples: 'Tubeless, tube, tyre',
      price: formatInr(s.puncture.price),
      features: ['One tyre puncture', '₹100 extra for additional tyres', 'Doorstep service'],
      linkTo: '/service',
      btnLabel: 'Book Now',
    });
  }
  return cards;
}

/**
 * Build the car service cards from the snapshot.
 * Uses car basic/std/comp "from" prices + the running-repair flat ₹999.
 */
export function getCarServiceCards() {
  const c = snapshot.car || {};
  return [
    {
      label: 'Car Basic Service',
      examples: 'Oil, filter, 50-point check',
      price: formatInr(c.basic_service_from),
      features: ['Engine oil change', 'Oil filter replacement', '50-point inspection', 'Brake check'],
      linkTo: '/service',
      btnLabel: 'Book Now',
    },
    {
      label: 'Car AC Service',
      examples: 'Gas refill, cleaning, compressor',
      price: formatInr(snapshot.car_ac && snapshot.car_ac.from),
      features: ['12-point AC check', 'Condenser cleaning', 'Leak detection', '30-day cooling warranty'],
      linkTo: '/service',
      btnLabel: 'Book Now',
      highlight: true,
    },
    {
      label: 'Car Running Repair',
      examples: 'Breakdown, jump-start, puncture',
      price: formatInr(c.running_repair),
      features: ['Roadside breakdown', 'Jump-start', 'Puncture repair', 'Doorstep service'],
      linkTo: '/service',
      btnLabel: 'Book Now',
    },
  ];
}

/**
 * 3-tier comparison rows used by programmatic-service-page bike table.
 * Maps Basic/Standard/Comprehensive to real backend prices instead of
 * the old hardcoded ₹799/₹1,299/₹1,999.
 */
export function getBikeTiers() {
  const s = getBikeServices();
  return {
    basic:    { label: 'General Service',         price: formatInr(s.general_service ? s.general_service.price : 799) },
    standard: { label: 'General Service + Oil',   price: formatInr(s.general_service_with_oil ? s.general_service_with_oil.price : 1249) },
    comp:     { label: 'Full Service + Repair',   price: formatInr((s.general_service_with_oil ? s.general_service_with_oil.price : 1249) + (s.running_repair ? s.running_repair.price : 450)) },
  };
}

/** 3-tier comparison rows used by programmatic-service-page car table. */
export function getCarTiers() {
  const c = snapshot.car || {};
  return {
    basic:    { label: 'Basic Service',         price: formatInr(c.basic_service_from) },
    standard: { label: 'Standard Service',      price: formatInr(c.standard_service_from) },
    comp:     { label: 'Comprehensive Service', price: formatInr(c.comprehensive_service_from) },
  };
}

const api = {
  PRICING,
  formatInr,
  getStartingPrice,
  startingPriceLabel,
  getServiceStartingPrice,
  getBikeServices,
  getAcTiers,
  getBikeServiceCards,
  getCarServiceCards,
  getBikeTiers,
  getCarTiers,
};

export default api;
